/**
 * Fixed-timestep loop with an accumulator.
 *
 * Physics always advances in steps of fixedDtS, independent of display rate.
 * Frame time is clamped to maxSubsteps * fixedDtS before it enters the
 * accumulator. Excess time is dropped, so a slow frame cannot trigger a
 * death spiral where each frame needs more substeps than the last.
 *
 * Reference: Fiedler, G. (2004). "Fix Your Timestep!" gafferongames.com.
 */

export interface LoopOptions {
  /** Default 1/60. */
  fixedDtS: number;
  /** Default 4. */
  maxSubsteps: number;
  update(dtS: number): void;
  /** alpha = interpolation factor in [0, 1) between the last two physics states. */
  render(alpha: number): void;
  /**
   * Optional per-frame hook for instrumentation (HUD, governor).
   * cpuMs is the main-thread time spent in update + render this frame.
   */
  onFrame?(frameDeltaS: number, substeps: number, cpuMs: number): void;
}

export interface Stepper {
  /** Advance by one display frame. Returns the number of physics substeps run. */
  step(frameDeltaS: number): number;
  /** Drop accumulated time, e.g. after the tab was hidden. */
  resetAccumulator(): void;
}

export const DEFAULT_FIXED_DT_S = 1 / 60;
export const DEFAULT_MAX_SUBSTEPS = 4;

/** Pure accumulator logic, no rAF. Unit tests drive this directly. */
export function createStepper(opts: LoopOptions): Stepper {
  const dt = opts.fixedDtS;
  const maxFrameS = opts.maxSubsteps * dt;
  let accumulatorS = 0;

  return {
    step(frameDeltaS: number): number {
      const clamped = frameDeltaS > maxFrameS ? maxFrameS : frameDeltaS < 0 ? 0 : frameDeltaS;
      accumulatorS += clamped;
      let substeps = 0;
      while (accumulatorS >= dt && substeps < opts.maxSubsteps) {
        opts.update(dt);
        accumulatorS -= dt;
        substeps++;
      }
      // Rounding can leave accumulatorS a hair above dt after the cap. Keep alpha < 1.
      if (accumulatorS >= dt) accumulatorS = dt * 0.999999;
      opts.render(accumulatorS / dt);
      return substeps;
    },
    resetAccumulator(): void {
      accumulatorS = 0;
    },
  };
}

export interface Loop {
  start(): void;
  stop(): void;
  isRunning(): boolean;
  resetAccumulator(): void;
}

export function createLoop(opts: LoopOptions): Loop {
  const stepper = createStepper(opts);
  let running = false;
  let rafId = 0;
  let lastMs = -1;

  // Created once. rAF reuses this function every frame, so no closure per frame.
  const tick = (nowMs: number): void => {
    if (!running) return;
    const deltaS = lastMs < 0 ? 0 : (nowMs - lastMs) / 1000;
    lastMs = nowMs;
    const t0 = performance.now();
    const substeps = stepper.step(deltaS);
    const cpuMs = performance.now() - t0;
    opts.onFrame?.(deltaS, substeps, cpuMs);
    rafId = requestAnimationFrame(tick);
  };

  return {
    start(): void {
      if (running) return;
      running = true;
      lastMs = -1;
      stepper.resetAccumulator();
      rafId = requestAnimationFrame(tick);
    },
    stop(): void {
      running = false;
      cancelAnimationFrame(rafId);
    },
    isRunning: () => running,
    resetAccumulator: () => {
      stepper.resetAccumulator();
    },
  };
}
